import XlsDownload from "@dana/Icons/XlsDownload";
import { themeBackgrounds } from "@dana/MobiusOverrides/styles/colors";
import { fontSizes, fontWeights } from "@dana/MobiusOverrides/styles/typography";
import ApplicationStateDana from "@dana/Store/DanaApplicationState";
import exportCredits from "@dana/Store/Data/ViewCredit/Handlers/ExportCredits";
import updateCreditFilterFields from "@dana/Store/Data/ViewCredit/Handlers/ViewCreditFilterHandler";
import getCreditMomos, { GetCreditMemosParameter } from "@dana/Store/Data/ViewCredit/Handlers/ViewCreditHandler";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { convertDateToDateOnlyString } from "@insite/client-framework/Common/Utilities/DateUtilities";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import TextField from "@insite/mobius/TextField";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface ViewCreditResultCountStyle {
    wrapper?: InjectableCss;
    viewCreditSearchBy?: GridContainerProps;
    viewCreditText?: TypographyPresentationProps;
    searchByTextWithExport?: GridItemProps;
    searchByText?: TypographyPresentationProps;
    creditMemosDateFilter?: GridItemProps;
    creditMemosDateFilterText?: GridItemProps;
    viewCreditDateFilterContainer?: GridContainerProps;
    creditMemoDateViewText?: TypographyPresentationProps;
    searchButtonViewCredit?: ButtonPresentationProps;
    clearButtonViewCredit?: ButtonPresentationProps;
    viewCreditDate?: DatePickerPresentationProps;
    viewCreditFilterButton?: GridItemProps;
    creditMemodatePickerFilter?: GridItemProps;
    creditViewMemoSearchFilter?: GridItemProps;
    creditMemoContainer?: GridContainerProps;
}

export const viewCreditResultCountStyle: ViewCreditResultCountStyle = {
    wrapper: {
        css: css`
            padding: 16px 0;
        `
    },
    creditMemosDateFilter: {
        width: 12,
        css: css`
            gap: 16px;
            align-items: center;
            padding: 0;
        `,
    },
    creditMemodatePickerFilter: {
        width: [12, 12, 4, 4, 4],
        css: css`
            gap: 12px;
            > div {
                flex: 1;
            }
        `,
    },
    creditMemoContainer: {
        gap: 12,
    },
    viewCreditFilterButton: {
        width: 12,

        css: css`
            gap: 15px;
            justify-content: end;
        `,
    },
    creditViewMemoSearchFilter: {
        width: [12, 6, 4, 4, 4],
    },
    creditMemosDateFilterText: {
        width: 12,
        css: css`
            padding: 16px 0;
            gap: 8px;
            align-items: center;
        `,
    },
    viewCreditSearchBy: {
        gap: 0,
    },
    searchByText: {
        weight: fontWeights.bold,
        transform: "uppercase",
    },
    viewCreditText: {
        variant: "h3",
        css: css`
            width: 100%;
            color: ${themeBackgrounds.darkGray};
            font-weight: ${fontWeights.regular};
            margin-bottom: 16px;
        `,
    },
    searchByTextWithExport: {
        width: 12,
        css: css`
            justify-content: space-between;
        `,
    },
    viewCreditDateFilterContainer: {
        gap: 0,
    },
    searchButtonViewCredit: {
        css: css`
            min-height: auto;
            padding: 5px 24px;
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            width: auto;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    creditMemoDateViewText: {
        css: css`
            font-size: ${fontSizes.xSmall};
            color: ${themeBackgrounds.darkGray};
        `,
    },
    clearButtonViewCredit: {
        variant: "secondary",
        css: css`
            min-height: auto;
            padding: 5px 24px;
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            width: auto;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    viewCreditDate: {
        cssOverrides: {
            datePicker: css`
                width: 100%;
            `,
            formField: css`
                width: 100%;
                [class*="FormFieldLabel"] + [class*="FormInputWrapper"] {
                    margin-top: 4px;
                }
                .react-datetime-picker  {
                    height: 42px;
                }
            `,
        },
    },
};
const mapStateToProps = (state: ApplicationStateDana) => ({
    viewCreditState: state.viewCreditState,
});

const mapDispatchToProps = {
    updateCreditFilterFields,
    getCreditMomos,
    exportCredits,
};
type Props = HasToasterContext & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

const styles = viewCreditResultCountStyle;

const ViewCreditSearchBy: React.FC<Props> = ({
    exportCredits,
    getCreditMomos,
    updateCreditFilterFields,
    viewCreditState,
    toaster,
}) => {
    const [fromDate, setFromDate] = useState<Date | null>();
    const [toDate, setToDate] = useState<Date | null>();

    useEffect(() => {
        if (typeof window !== "undefined") {
            const params = new URLSearchParams(window.location.search);
            const today = new Date();
            const lastYearDate = new Date(today);
            lastYearDate.setDate(today.getDate() - 365);
            setFromDate(lastYearDate);
            setToDate(today);

            updateCreditFilterFields({
                fromDate: convertDateToDateOnlyString(lastYearDate),
                toDate: convertDateToDateOnlyString(today),
                partNumber: params.get("partNumber") ?? "",
                creditMemoNumber: params.get("creditMemoNumber") ?? undefined,
                primaryPONumber: params.get("primaryPONumber") ?? undefined,
                secondaryPoNumber: params.get("secondaryPoNumber") ?? undefined,
                creditMemoTotal: params.get("creditMemoTotal") ?? undefined,
            });
        }
    }, []);
    const fromDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        if (selectedDay) {
            setFromDate(selectedDay);
            updateCreditFilterFields({
                ...viewCreditState.filter,
                fromDate: convertDateToDateOnlyString(selectedDay),
            });
            // If the new start date is after the existing end date, clear the end date
            if (toDate && selectedDay > toDate) {
                setToDate(null);
            }
        }
    };

    const toDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        if (selectedDay) {
            setToDate(selectedDay);
            updateCreditFilterFields({
                ...viewCreditState.filter,
                toDate: convertDateToDateOnlyString(selectedDay),
            });
        }
    };
    const searchCredits = () => {
        getCreditMomos({ ...viewCreditState.filter });
    };
    const clearSearch = () => {
        const today = new Date();
        const lastYearDate = new Date(today);
        lastYearDate.setDate(today.getDate() - 365);
        const filter = {
            fromDate: convertDateToDateOnlyString(lastYearDate),
            toDate: convertDateToDateOnlyString(today),
        };
        setFromDate(lastYearDate);
        setToDate(today);
        updateCreditFilterFields(filter);
        getCreditMomos(filter);
    };
    const creditViewHandler = () => {
        toaster.addToast({
            body: translate("Your request received successfully and is being processed."),
            messageType: "info",
        });
        exportCredits({});
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        updateCreditFilterFields({
            ...viewCreditState.filter,
            [name]: value,
        });
    };
    return (
        <StyledWrapper {...styles.wrapper}>
            <GridContainer {...styles.viewCreditSearchBy}>
                <GridItem width={12}>
                    <Typography className="view-credit-text" {...styles.viewCreditText}>
                        {translate("View Credits")}
                    </Typography>
                </GridItem>

                <GridItem {...styles.searchByTextWithExport}>
                    <Typography className="text-body-sm" {...styles.searchByText}>
                        {translate("Search By")}
                    </Typography>
                    {viewCreditState?.allCredits && (
                        <Button variant="tertiary" onClick={creditViewHandler}>
                            <Icon
                                src={XlsDownload}
                                title="Export to Excel"
                                color={themeBackgrounds.darkGray}
                                size={24}
                            />
                        </Button>
                    )}
                </GridItem>
            </GridContainer>

            <GridContainer {...styles.viewCreditDateFilterContainer}>
                <GridItem {...styles.creditMemosDateFilterText}>
                    <Typography className="text-body-sm">{translate("Credit Memo Date")}</Typography>

                    <Typography {...styles.creditMemoDateViewText}>
                        {translate("Change Credit Memo Dates to view more.")}
                    </Typography>
                </GridItem>

                <GridItem {...styles.creditMemosDateFilter} className="date-filter-viewcredit">
                    <GridContainer {...styles.creditMemoContainer}>
                        <GridItem {...styles.creditMemodatePickerFilter}>
                            <DatePicker
                                data-test-selector="orderHistory_filterFromDate"
                                {...styles.viewCreditDate}
                                label={translate("Date Range")}
                                selectedDay={fromDate || undefined}
                                onDayChange={fromDateChangeHandler}
                                dateTimePickerProps={{
                                    clearIcon: null,
                                    maxDate: toDate || undefined,
                                }}
                            />
                            <DatePicker
                                data-test-selector="orderHistory_filterToDate"
                                {...styles.viewCreditDate}
                                label={translate("To")}
                                selectedDay={toDate || undefined}
                                onDayChange={toDateChangeHandler}
                                dateTimePickerProps={{
                                    clearIcon: null,
                                    minDate: fromDate || undefined,
                                }}
                            />
                        </GridItem>
                        <GridItem {...styles.creditViewMemoSearchFilter}>
                            <TextField
                                data-test-selector="orderHistory_creditMemoNumber"
                                name="creditMemoNumber"
                                value={viewCreditState.filter.creditMemoNumber || ""}
                                label={translate("Credit Memo No")}
                                type="text"
                                onChange={handleChange}
                            />
                        </GridItem>

                        <GridItem {...styles.creditViewMemoSearchFilter}>
                            <TextField
                                data-test-selector="orderHistory_primaryPONumber"
                                name="primaryPONumber"
                                value={viewCreditState.filter.primaryPONumber || ""}
                                label={translate("Primary PO No")}
                                type="text"
                                onChange={handleChange}
                            />
                        </GridItem>

                        <GridItem {...styles.creditViewMemoSearchFilter}>
                            <TextField
                                data-test-selector="orderHistory_secondaryPoNumber"
                                name="secondaryPoNumber"
                                value={viewCreditState.filter.secondaryPoNumber || ""}
                                label={translate("Secondary PO No")}
                                type="text"
                                onChange={handleChange}
                            />
                        </GridItem>

                        <GridItem {...styles.creditViewMemoSearchFilter}>
                            <TextField
                                data-test-selector="orderHistory_partNumber"
                                name="partNumber"
                                value={viewCreditState.filter.partNumber || ""}
                                label={translate("Part No")}
                                type="text"
                                onChange={handleChange}
                            />
                        </GridItem>

                        <GridItem width={[12, 12, 4, 4, 4]}>
                            <TextField
                                data-test-selector="orderHistory_creditMemoTotal"
                                name="creditMemoTotal"
                                value={viewCreditState.filter.creditMemoTotal || ""}
                                label={translate("Credit Memo Total")}
                                type="text"
                                onChange={handleChange}
                            />
                        </GridItem>

                        <GridItem {...styles.viewCreditFilterButton}>
                            <Button {...styles.searchButtonViewCredit} onClick={searchCredits}>
                                {translate("Search")}
                            </Button>
                            <Button {...styles.clearButtonViewCredit} onClick={clearSearch}>
                                {translate("Clear")}
                            </Button>
                        </GridItem>
                    </GridContainer>
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};
export default connect(mapStateToProps, mapDispatchToProps)(withToaster(ViewCreditSearchBy));
